import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Zap, Trophy, Lock, RefreshCw, ChevronDown, ChevronUp,
  Star, CheckCircle, Target, AlertTriangle,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { ScoreBar, ScoreCircle } from '../../components/ui/ScoreBar'
import { anonApi } from '../../lib/api'
import { clearAnonSession, getAnonSession } from '../../lib/auth'
import { InlineLoader } from '../../components/ui/LoadingSpinner'
import { getScoreBg } from '../../lib/utils'

// --- Helpers ---

function getConfidence(score, t) {
  if (score >= 90) return { label: t('anonResults.confidence.veryHigh'), color: 'text-green-600' }
  if (score >= 75) return { label: t('anonResults.confidence.high'), color: 'text-green-500' }
  if (score >= 60) return { label: t('anonResults.confidence.moderate'), color: 'text-yellow-600' }
  return { label: t('anonResults.confidence.low'), color: 'text-red-500' }
}

// --- Components ---

function DecisionBanner({ recommendedCandidates }) {
  const { t } = useLocale()
  if (!recommendedCandidates.length) return null
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-green-600 rounded-2xl p-4 mb-6 flex items-center justify-between gap-4"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
          <Target className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-bold text-white text-sm">
            {recommendedCandidates.length === 1
              ? t('anonResults.oneWorthReviewing')
              : t('anonResults.manyWorthReviewing', { count: recommendedCandidates.length })}
          </p>
          <p className="text-green-200 text-xs mt-0.5">
            {recommendedCandidates.map(c => c.name).join(', ')} - {t('anonResults.basedOnRequirements')}
          </p>
        </div>
      </div>
      <CheckCircle className="w-6 h-6 text-green-300 shrink-0" />
    </motion.div>
  )
}

function TopCandidateCard({ candidate }) {
  const { t } = useLocale()
  const confidence = getConfidence(candidate.matchScore, t)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="card p-6 mb-5 border-2 border-green-200 bg-gradient-to-r from-green-50 to-white"
    >
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <ScoreCircle score={candidate.matchScore} size="lg" />
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center">
            <Star className="w-3 h-3 text-white fill-white" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{t('anonResults.highestScore')}</span>
            <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Target className="w-3 h-3" />
              {t('anonResults.reviewFirst')}
            </span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-0.5">{candidate.name || t('anonResults.candidateFallback', { number: 1 })}</h3>
          <p className={`text-xs font-medium mb-2 ${confidence.color}`}>{confidence.label}</p>
          {candidate.whyThisMatch && (
            <p className="text-sm text-green-800 font-medium mb-2">✓ {candidate.whyThisMatch}</p>
          )}
          {candidate.summary && (
            <p className="text-sm text-gray-600 leading-relaxed">{candidate.summary}</p>
          )}
        </div>
      </div>
      {candidate.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-green-100">
          <span className="text-xs text-green-700 font-semibold mr-1">{t('anonResults.matchedSkills')}</span>
          {candidate.skills.slice(0, 6).map((skill) => (
            <span key={skill} className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
              ✓ {skill}
            </span>
          ))}
          {candidate.skills.length > 6 && (
            <span className="text-xs text-green-600 font-medium">{t('anonResults.more', { count: candidate.skills.length - 6 })}</span>
          )}
        </div>
      )}
    </motion.div>
  )
}

function CandidateCard({ candidate, index, isExpanded, onToggle, isRecommended }) {
  const { t } = useLocale()
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="card overflow-hidden"
    >
      <div className="p-4 cursor-pointer" onClick={() => onToggle(candidate.candidateId)}>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-gray-500">#{index + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 mb-2">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {candidate.name || t('anonResults.candidateFallback', { number: index + 1 })}
                </h3>
                {isRecommended && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1 mt-0.5">
                    <Target className="w-3 h-3" />
                    {t('anonResults.higherScore')}
                  </span>
                )}
                {candidate.matchScore < 60 && (
                  <span className="text-xs text-red-500 font-medium flex items-center gap-1 mt-0.5">
                    <AlertTriangle className="w-3 h-3" />
                    {t('anonResults.gapsDetected')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-sm font-bold px-3 py-1 rounded-full border ${getScoreBg(candidate.matchScore)}`}>
                  {t('anonResults.match', { score: candidate.matchScore })}
                </span>
                {isExpanded
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />
                }
              </div>
            </div>
            <ScoreBar score={candidate.matchScore} size="sm" />
            {candidate.whyThisMatch && (
              <p className="text-xs text-gray-500 mt-1.5 italic">{candidate.whyThisMatch}</p>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="border-t border-gray-100 p-4 bg-gray-50"
        >
          {candidate.summary && (
            <div className="bg-brand-50 rounded-xl p-3 mb-3 border border-brand-100">
              <p className="text-sm text-brand-800 leading-relaxed">{candidate.summary}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {candidate.experienceYears > 0 && (
              <div className="bg-white rounded-xl p-3 border border-gray-100">
                <p className="text-xs text-gray-500">{t('anonResults.experience')}</p>
                <p className="font-semibold text-gray-900">{t('anonResults.years', { count: candidate.experienceYears })}</p>
              </div>
            )}
            {candidate.education && (
              <div className="bg-white rounded-xl p-3 border border-gray-100">
                <p className="text-xs text-gray-500">{t('anonResults.education')}</p>
                <p className="font-semibold text-gray-900 text-sm truncate">{candidate.education}</p>
              </div>
            )}
          </div>
          {candidate.skills?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 mb-2">{t('anonResults.matchedSkillsHeading')}</p>
              <div className="flex flex-wrap gap-1.5">
                {candidate.skills.map((skill) => (
                  <span key={skill} className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                    ✓ {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
          {candidate.missingSkills?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">{t('anonResults.missingSkillsHeading')}</p>
              <div className="flex flex-wrap gap-1.5">
                {candidate.missingSkills.map((skill) => (
                  <span key={skill} className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
                    ✗ {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}

function SignUpCTA({ recommendedCandidates, navigate }) {
  const { t } = useLocale()
  const count = recommendedCandidates.length
  const names = recommendedCandidates.map(c => c.name)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="card p-6 border-2 border-brand-200 bg-gradient-to-b from-brand-50 to-white"
    >
      <div className="text-center mb-5">
        <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-3">
          <Lock className="w-6 h-6 text-brand-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {count > 0
            ? t('anonResults.saveFound', { count, plural: count > 1 ? 's' : '' })
            : t('anonResults.saveExpired')}
        </h3>
        <p className="text-gray-500 text-sm max-w-md mx-auto">
          {count > 0
            ? t('anonResults.createAccountFound', { names: names.join(t('anonResults.nameJoiner')) })
            : t('anonResults.createAccountExpired')}
        </p>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
        <p className="text-xs font-semibold text-gray-500 mb-3">{t('anonResults.freeAccountTitle')}</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            t('anonResults.accountBenefits.0'),
            t('anonResults.accountBenefits.1'),
            t('anonResults.accountBenefits.2'),
            t('anonResults.accountBenefits.3'),
            t('anonResults.accountBenefits.4'),
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="primary" fullWidth size="lg" onClick={() => navigate('/signup')} icon={<Zap className="w-4 h-4" />}>
          {t('anonResults.saveContinue')}
        </Button>
        <Button variant="secondary" size="lg" onClick={() => navigate('/upload')} icon={<RefreshCw className="w-4 h-4" />}>
          {t('anonResults.newUpload')}
        </Button>
      </div>
      <p className="text-xs text-gray-400 text-center mt-3">
        {t('anonResults.noCard')}
      </p>
    </motion.div>
  )
}

// --- Main Page ---

export function AnonResults() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const [session, setSession] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    loadResults()
  }, [])

  async function loadResults() {
    const stored = getAnonSession()
    if (!stored?.sessionId) {
      navigate('/upload')
      return
    }
    setSession(stored)
    try {
      const data = await anonApi.getSession(stored)
      const sorted = [...(data.candidates || [])].sort((a, b) => b.matchScore - a.matchScore)
      setCandidates(sorted)
      setLoading(false)
    } catch (err) {
      const expired = err?.status === 410 || err?.payload?.expired
      if (expired) {
        clearAnonSession()
      }
      setError({
        expired,
        message: expired
          ? t('anonResults.expiredMessage')
          : t('anonResults.loadError'),
      })
      setLoading(false)
    }
  }

  function toggleExpand(id) {
    setExpanded(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center">
        <InlineLoader text={t('anonResults.loading')} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-md">
          <p className="text-red-600 mb-4">{error.message}</p>
          <Button onClick={() => navigate('/upload')}>
            {error.expired ? t('anonResults.startNewUpload') : t('anonResults.tryAgain')}
          </Button>
        </div>
      </div>
    )
  }

  if (!candidates.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-md">
          <p className="text-gray-500 mb-4">{t('anonResults.noCandidates')}</p>
          <Button variant="primary" onClick={() => navigate('/upload')}>{t('anonResults.tryAnother')}</Button>
        </div>
      </div>
    )
  }

  const recommendedCandidates = candidates.slice(0, 3).filter(c => c.matchScore >= 60)
  const topCandidate = candidates[0]

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-xl bg-brand-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">WorkflowPro</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitch />
            <Button variant="ghost" size="sm" onClick={() => navigate('/upload')} icon={<RefreshCw className="w-4 h-4" />}>
              {t('anonResults.newUpload')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/signup')}>
              {t('anonResults.saveResultsFree')}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Decision banner */}
        <DecisionBanner recommendedCandidates={recommendedCandidates} />

        {/* Header stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
            <Trophy className="w-4 h-4" />
            {t('anonResults.candidatesRanked', { count: candidates.length })}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {session?.jobTitle ? t('anonResults.resultsFor', { jobTitle: session.jobTitle }) : t('anonResults.rankedCandidates')}
          </h1>
          <p className="text-gray-500">{t('anonResults.rankedByScore')}</p>
        </motion.div>

        {/* Top candidate */}
        <TopCandidateCard candidate={topCandidate} />

        {/* All candidates */}
        <div className="space-y-3 mb-8">
          {candidates.map((candidate, index) => (
            <CandidateCard
              key={candidate.candidateId}
              candidate={candidate}
              index={index}
              isExpanded={expanded.includes(candidate.candidateId)}
              onToggle={toggleExpand}
              isRecommended={recommendedCandidates.some(r => r.candidateId === candidate.candidateId)}
            />
          ))}
        </div>

        {/* Sign up CTA */}
        <SignUpCTA recommendedCandidates={recommendedCandidates} navigate={navigate} />
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 shadow-lg">
        <Button
          variant="primary"
          size="lg"
          onClick={() => navigate('/signup')}
          icon={<Zap className="w-4 h-4" />}
          className="shadow-xl"
        >
          {t('anonResults.saveResultsFree')}
        </Button>
      </div>
    </div>
  )
}
